import { DAYS_ORDER } from "./constants";
import { clampDateToMidnight, startOfWeekMonday } from "./dateUtils";

export const SCHEDULE_ROUTE_BASE = "/scheduling/schedule";

function normalizePathname(pathname = "") {
  const trimmed = String(pathname || "").trim();
  if (!trimmed) return "/";
  if (trimmed === "/") return "/";
  return trimmed.replace(/\/+$/, "");
}

function createLocalDate(year, monthIndex, day) {
  const date = new Date(year, monthIndex, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== monthIndex ||
    date.getDate() !== day
  ) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
}

function parseDateToken(token) {
  const match = String(token || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex) || !Number.isInteger(day)) return null;
  return createLocalDate(year, monthIndex, day);
}

function parseMonthToken(token) {
  const match = String(token || "").match(/^(\d{4})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isInteger(year) || monthIndex < 0 || monthIndex > 11) return null;
  return createLocalDate(year, monthIndex, 1);
}

function parseYearToken(token) {
  const match = String(token || "").match(/^(\d{4})$/);
  if (!match) return null;
  const year = Number(match[1]);
  if (!Number.isInteger(year)) return null;
  return createLocalDate(year, 0, 1);
}

function getIsoWeekInfo(dateInput) {
  const base = clampDateToMidnight(dateInput);
  const utc = new Date(Date.UTC(base.getFullYear(), base.getMonth(), base.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const isoYear = utc.getUTCFullYear();
  const yearStart = new Date(Date.UTC(isoYear, 0, 1));
  const week = Math.ceil((((utc - yearStart) / 86400000) + 1) / 7);
  return { isoYear, week };
}

function parseIsoWeekToken(token) {
  const match = String(token || "").match(/^(\d{4})-W(\d{2})$/);
  if (!match) return null;
  const isoYear = Number(match[1]);
  const week = Number(match[2]);
  if (!Number.isInteger(isoYear) || !Number.isInteger(week) || week < 1 || week > 53) return null;

  const jan4 = createLocalDate(isoYear, 0, 4);
  if (!jan4) return null;
  const jan4DayIndex = (jan4.getDay() + 6) % 7;
  const weekOneMonday = new Date(jan4);
  weekOneMonday.setDate(jan4.getDate() - jan4DayIndex);
  weekOneMonday.setHours(0, 0, 0, 0);

  const result = new Date(weekOneMonday);
  result.setDate(weekOneMonday.getDate() + (week - 1) * 7);
  result.setHours(0, 0, 0, 0);

  const roundTrip = getIsoWeekInfo(result);
  if (roundTrip.isoYear !== isoYear || roundTrip.week !== week) return null;
  return result;
}

function formatDateToken(dateInput) {
  const date = clampDateToMidnight(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatMonthToken(dateInput) {
  const date = clampDateToMidnight(dateInput);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function formatIsoWeekToken(dateInput) {
  const { isoYear, week } = getIsoWeekInfo(dateInput);
  return `${isoYear}-W${String(week).padStart(2, "0")}`;
}

function getDayViewModeForDate(dateInput) {
  const date = clampDateToMidnight(dateInput);
  const dayKey = DAYS_ORDER.find((candidate) => {
    const indexMap = {
      sunday: 0,
      monday: 1,
      tuesday: 2,
      wednesday: 3,
      thursday: 4,
      friday: 5,
      saturday: 6,
    };
    return indexMap[candidate] === date.getDay();
  });
  return dayKey || "monday";
}

export function parseScheduleRoute(pathname = "", search = "") {
  const normalized = normalizePathname(pathname);
  if (normalized !== SCHEDULE_ROUTE_BASE && !normalized.startsWith(`${SCHEDULE_ROUTE_BASE}/`)) {
    return null;
  }

  const params = new URLSearchParams(search || "");
  const isMasterMode = params.get("mode") === "master";
  const suffix = normalized.slice(SCHEDULE_ROUTE_BASE.length).replace(/^\/+/, "");

  if (!suffix) {
    return {
      matches: true,
      isExplicit: false,
      isMasterMode,
    };
  }

  const [viewToken, valueToken] = suffix.split("/");
  if (!viewToken || !valueToken) {
    return {
      matches: true,
      isExplicit: false,
      isMasterMode,
    };
  }

  if (viewToken === "day") {
    const date = parseDateToken(valueToken);
    if (!date) return { matches: true, isExplicit: false, isMasterMode };
    return {
      matches: true,
      isExplicit: true,
      isMasterMode,
      viewMode: getDayViewModeForDate(date),
      weekStart: startOfWeekMonday(date),
      dayDate: date,
    };
  }

  if (viewToken === "week") {
    const weekStart = parseIsoWeekToken(valueToken);
    if (!weekStart) return { matches: true, isExplicit: false, isMasterMode };
    return {
      matches: true,
      isExplicit: true,
      isMasterMode,
      viewMode: "week",
      weekStart,
    };
  }

  if (viewToken === "month") {
    const monthCursor = parseMonthToken(valueToken);
    if (!monthCursor) return { matches: true, isExplicit: false, isMasterMode };
    return {
      matches: true,
      isExplicit: true,
      isMasterMode,
      viewMode: "month",
      monthCursor,
      weekStart: startOfWeekMonday(monthCursor),
    };
  }

  if (viewToken === "year") {
    const yearCursor = parseYearToken(valueToken);
    if (!yearCursor) return { matches: true, isExplicit: false, isMasterMode };
    return {
      matches: true,
      isExplicit: true,
      isMasterMode,
      viewMode: "year",
      yearCursor,
      weekStart: startOfWeekMonday(yearCursor),
    };
  }

  return {
    matches: true,
    isExplicit: false,
    isMasterMode,
  };
}

export function buildScheduleRoute({
  viewMode,
  weekStart,
  currentDayDate,
  monthCursor,
  yearCursor,
  isMasterMode = false,
}) {
  let pathname = SCHEDULE_ROUTE_BASE;

  if (viewMode === "month") {
    pathname = `${SCHEDULE_ROUTE_BASE}/month/${formatMonthToken(monthCursor || new Date())}`;
  } else if (viewMode === "year") {
    pathname = `${SCHEDULE_ROUTE_BASE}/year/${(yearCursor || new Date()).getFullYear()}`;
  } else if (DAYS_ORDER.includes(viewMode)) {
    pathname = `${SCHEDULE_ROUTE_BASE}/day/${formatDateToken(currentDayDate || weekStart || new Date())}`;
  } else {
    pathname = `${SCHEDULE_ROUTE_BASE}/week/${formatIsoWeekToken(weekStart || new Date())}`;
  }

  const params = new URLSearchParams();
  if (isMasterMode) params.set("mode", "master");
  const search = params.toString();

  return search ? `${pathname}?${search}` : pathname;
}
